import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  #constraintMutationObserver = null;
  #isApplyingConstraints = false;
  #handleOrderConstraintsReady = () => {
    this.applyResolvedConstraints();
  };

  #observeConstraintMutations() {
    const { quantityInput } = this.refs;

    this.#constraintMutationObserver?.disconnect();
    this.#constraintMutationObserver = new MutationObserver((mutations) => {
      if (this.#isApplyingConstraints) return;

      const maxChanged = mutations.some((mutation) => mutation.attributeName === 'max');
      if (!maxChanged) return;

      this.#syncCanonicalMaxAttribute();
      this.updateCartQuantity();
    });

    this.#constraintMutationObserver.observe(quantityInput, {
      attributes: true,
      attributeFilter: ['max'],
    });
  }

  #syncCanonicalMaxAttribute() {
    const { quantityInput } = this.refs;
    const canonicalMax = quantityInput.dataset.pgCanonicalMax || '';

    if (canonicalMax) {
      if (quantityInput.getAttribute('max') !== canonicalMax) {
        quantityInput.setAttribute('max', canonicalMax);
      }
      return;
    }

    if (quantityInput.hasAttribute('max')) {
      quantityInput.removeAttribute('max');
    }
  }

  getResolvedOrderConstraints(min, step) {
    const { quantityInput } = this.refs;
    const explicitMin = parseIntOrDefault(quantityInput.dataset.pgMinConstraint, 0);
    const explicitStep = parseIntOrDefault(quantityInput.dataset.pgMultipleConstraint, 0);
    const productRef = (quantityInput.dataset.pgProductRef || '').trim();
    const fallbackConstraints = productRef ? globalThis.PG_ORDER_CONSTRAINTS?.[productRef] : null;
    const fallbackMin = parseIntOrDefault(fallbackConstraints?.minimumOrder, 0);
    const fallbackStep = parseIntOrDefault(fallbackConstraints?.multiple, 0);

    const resolvedMin = explicitMin > 1 ? explicitMin : fallbackMin > 1 ? fallbackMin : parseIntOrDefault(min, 1);
    const resolvedStep =
      explicitStep > 1 ? explicitStep : fallbackStep > 1 ? fallbackStep : parseIntOrDefault(step, 1);

    return {
      min: resolvedMin.toString(),
      step: resolvedStep.toString(),
    };
  }

  connectedCallback() {
    super.connectedCallback();
    globalThis.addEventListener('pg:order-constraints:ready', this.#handleOrderConstraintsReady);

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.applyResolvedConstraints();
    this.#observeConstraintMutations();
    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    globalThis.removeEventListener('pg:order-constraints:ready', this.#handleOrderConstraintsReady);
    this.#constraintMutationObserver?.disconnect();
    this.#constraintMutationObserver = null;
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    const resolvedConstraints = this.getResolvedOrderConstraints(min, step);
    const resolvedMin = resolvedConstraints.min;
    const resolvedStep = resolvedConstraints.step;
    const resolvedMax = max ? max.toString() : '';

    quantityInput.dataset.pgCanonicalMax = resolvedMax;

    this.#isApplyingConstraints = true;
    try {
      quantityInput.min = resolvedMin;
      if (resolvedMax) {
        quantityInput.max = resolvedMax;
      } else {
        quantityInput.removeAttribute('max');
      }
      quantityInput.step = resolvedStep;
    } finally {
      this.#isApplyingConstraints = false;
    }

    const newMin = parseIntOrDefault(resolvedMin, 1);
    const newStep = parseIntOrDefault(resolvedStep, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  applyResolvedConstraints() {
    const { quantityInput } = this.refs;
    this.updateConstraints(quantityInput.min || '1', quantityInput.max || null, quantityInput.step || '1');
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;
    const canonicalMax = quantityInput.dataset.pgCanonicalMax || '';

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: canonicalMax ? parseIntOrDefault(canonicalMax, null) : null,
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Clears any custom validation message from the quantity input.
   */
  clearValidationMessage() {
    this.refs.quantityInput.setCustomValidity('');
  }

  /**
   * Builds a localized quantity validation message based on minimum/multiple rules.
   * @param {number} rawValue
   * @returns {string}
   */
  getConstraintValidationMessage(rawValue) {
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();

    const minimumTemplate = quantityInput.dataset.pgMinimumMessage || '';
    const multipleTemplate = quantityInput.dataset.pgMultipleMessage || '';

    if (!Number.isFinite(rawValue) || rawValue < min) {
      return minimumTemplate.replaceAll('__MINIMUM__', min.toString());
    }

    if ((rawValue - min) % step !== 0) {
      return multipleTemplate
        .replaceAll('__MINIMUM__', min.toString())
        .replaceAll('__INCREMENT__', step.toString());
    }

    return '';
  }

  /**
   * Validates the current quantity against minimum and multiple constraints.
   * @param {{report?: boolean, rawValue?: number}} [options]
   * @returns {boolean}
   */
  validateCurrentValue(options = {}) {
    const { quantityInput } = this.refs;
    const rawValue = Number.isFinite(options.rawValue) ? options.rawValue : parseInt(quantityInput.value, 10);
    const validationMessage = this.getConstraintValidationMessage(rawValue);

    quantityInput.setCustomValidity(validationMessage);

    if (validationMessage && options.report) {
      quantityInput.reportValidity();
    }

    return validationMessage === '';
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const rawQuantity = parseInt(event.target.value, 10);

    if (!this.validateCurrentValue({ report: true, rawValue: rawQuantity })) {
      this.updateButtonStates();
      return;
    }

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, rawQuantity || 0));

    quantityInput.value = quantity.toString();
    quantityInput.setCustomValidity('');
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
